/**
 * custom-cart-related.js — Free-shipping related-products upsell
 *
 * Surfaces native Shopify product recommendations, merged across all cart items,
 * scored toward a free-shipping threshold. Built on standard Shopify storefront
 * endpoints (/cart.js, /recommendations/products.json, the Section Rendering API)
 * plus window.Shopify globals, so it is portable across Hyper (FoxEcom) themes.
 * Where the host theme exposes FoxTheme helpers (e.g. money formatting) we use
 * them for store-consistent output, with a graceful fallback if absent.
 *
 * Because the native recommendations endpoint takes a single product seed, the
 * "merge across the whole cart" logic runs here, client-side.
 *
 * Exposes window.CartRelated (the library) and the <cart-recommendations-extended>
 * custom element ("Cart recommendations - extended" — reuses Hyper's cart widget
 * markup + FoxTheme carousel, driven by our pipeline). Also records
 * recently-viewed products (localStorage) on product pages, used as fallback seeds.
 *
 * Theme-specific touch points:
 *   - listens for `cart:updated` (overridable via data-updated-event) to re-run
 *   - renders card-product-list cards + FoxTheme.Carousel (Hyper)
 * Defaults match the Hyper theme; adjust for other Hyper-based themes.
 */
(function () {
  'use strict';

  if (window.CartRelated) return; // define once

  var RV_KEY = 'cart-related:recently-viewed';
  var RV_MAX = 20;

  /*
   * Session caches (module scope → survive the cart drawer re-rendering, which
   * destroys and recreates the <cart-recommendations-extended> element on every
   * cart change; a per-instance cache would be wiped each add). Keyed so they
   * stay valid as the cart changes:
   *   - recs: by product_id|intent|limit (recommendations are per-product,
   *     independent of cart contents — we exclude in-cart items later, at merge).
   *   - cards: themed card HTML by handle (product-static).
   * Reset naturally on full page navigation. The test sandbox passes its own
   * caches instead, so its cold/warm timings stay controlled.
   */
  var sessionRecsCache = {};
  var sessionCardCache = {};

  var DEFAULTS = {
    intent: 'related', // 'related' | 'complementary'
    perSeedLimit: 6, // 1..10 (Shopify max). 6 keeps per-seed work + payload small.
    maxSeeds: 3, // cap seeds → at most 3 parallel recs requests (well under host caps)
    limit: 6, // products shown in the strip
    hideWhenQualified: true, // hide once cart >= threshold
    includeRecentlyViewed: true, // use localStorage recently-viewed as fallback seeds
    renderMode: 'themed', // 'themed' (section-render card) | 'js' (lightweight)
    cardSectionId: 'custom-cart-related-card',
    minimumAmount: null, // free-shipping threshold in MAJOR units (e.g. dollars); null => no gap scoring
  };

  /* ---------------------------------------------------------------- helpers */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function currencyRate() {
    var rate = window.Shopify && window.Shopify.currency && Number(window.Shopify.currency.rate);
    return rate && !isNaN(rate) ? rate : 1;
  }

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  /*
   * Money formatter (input in cents). Prefers the Hyper theme's own
   * FoxTheme.Currency.formatMoney + the store's configured money format
   * (FoxTheme.settings.moneyFormat) so output matches the rest of the theme
   * (symbol placement, separators, multi-currency). Falls back to
   * Intl.NumberFormat, then a bare string, when FoxTheme isn't available.
   */
  function formatMoney(cents) {
    try {
      if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
        var fmt = window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat;
        return window.FoxTheme.Currency.formatMoney(cents, fmt);
      }
    } catch (e) {
      /* fall through to generic formatting */
    }
    var amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode() }).format(amount);
    } catch (e2) {
      return '$' + amount.toFixed(2);
    }
  }

  /* Recommendations JSON `featured_image` may be a string URL or an image object. */
  function imageUrl(image, width) {
    var src = '';
    if (!image) return '';
    if (typeof image === 'string') src = image;
    else src = image.src || image.url || '';
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    width = width || 360;
    var sep = src.indexOf('?') === -1 ? '?' : '&';
    return src + sep + 'width=' + width;
  }

  function thresholdCents(minimumAmountMajor) {
    if (minimumAmountMajor == null || minimumAmountMajor === '') return null;
    var n = Number(minimumAmountMajor);
    if (isNaN(n) || n <= 0) return null;
    return Math.round(n * 100 * currencyRate());
  }

  /* ----------------------------------------------------------- recently-viewed */

  function getRecentlyViewed() {
    try {
      var raw = window.localStorage.getItem(RV_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function recordRecentlyViewed() {
    var isProductPage =
      /\/products\//.test(window.location.pathname) ||
      document.body.classList.contains('template-product') ||
      document.body.classList.contains('product-template');
    if (!isProductPage) return;

    var handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = handleMatch ? handleMatch[1] : null;

    var id = null;
    try {
      if (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        id = ShopifyAnalytics.meta.product.id;
      }
    } catch (e) {
      /* ignore */
    }
    if (!id) {
      var el = document.querySelector('[data-product-id]');
      if (el) id = Number(el.getAttribute('data-product-id'));
    }
    if (!id || !handle) return;

    var list = getRecentlyViewed().filter(function (item) {
      return item && item.id !== id;
    });
    list.unshift({ id: id, handle: handle, t: Date.now() });
    if (list.length > RV_MAX) list = list.slice(0, RV_MAX);

    try {
      window.localStorage.setItem(RV_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / unavailable — ignore */
    }
  }

  /* ---------------------------------------------------------------- fetches */

  function fetchCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* Returns [] (never rejects) so one bad seed never breaks the merge.
     Optional `cache` (keyed by product/intent/limit) is consulted first and
     populated on success — so repeat runs (e.g. each add-to-cart re-render)
     skip the network for already-fetched seeds. */
  function fetchRecommendations(productId, opts, cache) {
    var key = productId + '|' + opts.intent + '|' + opts.perSeedLimit;
    if (cache && cache[key]) return Promise.resolve(cache[key]);
    var url =
      root() +
      'recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&intent=' +
      encodeURIComponent(opts.intent) +
      '&limit=' +
      encodeURIComponent(opts.perSeedLimit);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) return { products: [] }; // 404 unpublished / 422 bad param
        return r.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        if (cache) cache[key] = products; // cache successes (incl. known-empty)
        return products;
      })
      .catch(function () {
        return []; // transient error — don't cache, allow retry next run
      });
  }

  /* Full product JSON via {handle}.js. Used to resolve seed metadata
     (title/image/url) for seeds that aren't cart lines — e.g. recently-viewed
     seeds, which we only know by id+handle. Resolves to null on failure. */
  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /* Themed card via the Section Rendering API. Cached by handle. */
  function fetchThemedCardHTML(handle, sectionId, cache) {
    if (cache && cache[handle] != null) return Promise.resolve(cache[handle]);
    var url = root() + 'products/' + handle + '?section_id=' + encodeURIComponent(sectionId);
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (text) {
        var html = '';
        if (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('.shopify-section');
          html = section ? section.innerHTML : '';
        }
        if (cache) cache[handle] = html;
        return html;
      })
      .catch(function () {
        return '';
      });
  }

  /* ------------------------------------------------------------- selection */

  function normalizeOptions(opts) {
    var o = {};
    for (var k in DEFAULTS) if (DEFAULTS.hasOwnProperty(k)) o[k] = DEFAULTS[k];
    if (opts) for (var j in opts) if (opts.hasOwnProperty(j) && opts[j] != null) o[j] = opts[j];
    o.perSeedLimit = Math.max(1, Math.min(10, Number(o.perSeedLimit) || 6));
    o.maxSeeds = Math.max(1, Number(o.maxSeeds) || 3);
    o.limit = Math.max(1, Number(o.limit) || 6);
    return o;
  }

  function gatherSeeds(cart, opts) {
    var cartProductIds = {};
    (cart.items || []).forEach(function (item) {
      cartProductIds[item.product_id] = true;
    });

    var seeds = [];
    var lines = (cart.items || []).slice().sort(function (a, b) {
      return (b.line_price || 0) - (a.line_price || 0);
    });
    lines.forEach(function (item) {
      if (item.gift_card) return;
      if (seeds.indexOf(item.product_id) === -1) seeds.push(item.product_id);
    });

    if (opts.includeRecentlyViewed) {
      getRecentlyViewed().forEach(function (rv) {
        var id = rv && rv.id;
        if (id && !cartProductIds[id] && seeds.indexOf(id) === -1) seeds.push(id);
      });
    }

    return { seeds: seeds.slice(0, opts.maxSeeds), cartProductIds: cartProductIds };
  }

  /*
   * Resolve to:
   *   { thresholdCents, gap, qualified, seeds, perSeed:[{seed,products}],
   *     candidates:[{product,votes,bestRank}], selected:[product,…] }
   * gap/qualified are null when no threshold is configured.
   */
  function selectProducts(cart, opts, recsCache) {
    opts = normalizeOptions(opts);
    var seedInfo = gatherSeeds(cart, opts);
    var seeds = seedInfo.seeds;
    var cartProductIds = seedInfo.cartProductIds;

    var tCents = thresholdCents(opts.minimumAmount);
    // Post-discount total: total_price reflects cart-level coupons; items_subtotal_price does
    // not, which would over-count progress toward free shipping once a coupon is applied.
    // Kept in lockstep with the free-shipping goal nudge (snippets/free-shipping-goal.liquid).
    var subtotal = cart.total_price || 0;
    var gap = tCents == null ? null : tCents - subtotal;
    var qualified = gap == null ? false : gap <= 0;

    return Promise.all(
      seeds.map(function (id) {
        return fetchRecommendations(id, opts, recsCache).then(function (products) {
          return { seed: id, products: products };
        });
      })
    ).then(function (perSeed) {
      var candidates = {};
      perSeed.forEach(function (res) {
        res.products.forEach(function (p, idx) {
          if (!p || cartProductIds[p.id] || p.available === false) return;
          var c = candidates[p.id];
          if (!c) c = candidates[p.id] = { product: p, votes: 0, bestRank: idx };
          c.votes += 1;
          if (idx < c.bestRank) c.bestRank = idx;
        });
      });

      var list = Object.keys(candidates).map(function (key) {
        return candidates[key];
      });

      var groupA = [];
      var groupB = [];
      list.forEach(function (c) {
        if (gap != null && gap > 0 && c.product.price >= gap) groupA.push(c);
        else groupB.push(c);
      });

      // Group A: one add crosses the line — prefer the smallest sufficient overshoot.
      groupA.sort(function (a, b) {
        var oa = a.product.price - gap;
        var ob = b.product.price - gap;
        if (oa !== ob) return oa - ob;
        return b.votes - a.votes;
      });
      // Group B: cross-seed relevance first, then bigger contribution, then rank.
      groupB.sort(function (a, b) {
        if (b.votes !== a.votes) return b.votes - a.votes;
        if (b.product.price !== a.product.price) return b.product.price - a.product.price;
        return a.bestRank - b.bestRank;
      });

      var ordered = groupA.concat(groupB);
      var selected = ordered.slice(0, opts.limit).map(function (c) {
        return c.product;
      });

      return {
        thresholdCents: tCents,
        gap: gap,
        qualified: qualified,
        seeds: seeds,
        perSeed: perSeed,
        candidates: ordered,
        selected: selected,
      };
    });
  }

  /* ------------------------------------------------------------- add to cart */

  function addToCart(variantId, quantity) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
    }).then(function (r) {
      return r.json();
    });
  }

  function firstAvailableVariantId(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available !== false) return product.variants[i].id;
    }
    return product.variants[0].id;
  }

  /* Add a recommended product. Uses its first available variant; falls back to
     fetching {handle}.js when the recommendations payload lacks variants. */
  function addProduct(product) {
    var vid = firstAvailableVariantId(product);
    if (vid) return addToCart(vid, 1);
    return fetch(root() + 'products/' + product.handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (full) {
        var id = firstAvailableVariantId(full);
        if (!id) throw new Error('No available variant for ' + product.handle);
        return addToCart(id, 1);
      });
  }

  /* --------------------------------------------------------------- rendering */

  function buildJsCard(product, gap) {
    var card = document.createElement('div');
    card.className = 'cart-related__card';
    card.setAttribute('data-product-id', product.id);

    var completes = gap != null && gap > 0 && product.price >= gap;
    var url = product.url || root() + 'products/' + product.handle;

    var media =
      '<a class="cart-related__media" href="' +
      url +
      '" tabindex="-1" aria-hidden="true">' +
      (product.featured_image
        ? '<img loading="lazy" src="' +
          imageUrl(product.featured_image, 360) +
          '" alt="' +
          (product.title || '').replace(/"/g, '&quot;') +
          '" width="180" height="180">'
        : '') +
      '</a>';

    var info =
      '<div class="cart-related__info">' +
      '<a class="cart-related__title" href="' +
      url +
      '">' +
      (product.title || '') +
      '</a>' +
      '<div class="cart-related__price">' +
      formatMoney(product.price) +
      '</div>' +
      (completes ? '<div class="cart-related__flag">✓ gets free shipping</div>' : '') +
      '<button type="button" class="btn btn--primary cart-related__add" data-cart-related-add>Add</button>' +
      '</div>';

    card.innerHTML = media + info;

    var btn = card.querySelector('[data-cart-related-add]');
    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      addProduct(product)
        .then(function () {
          btn.textContent = 'Added ✓';
          card.dispatchEvent(
            new CustomEvent('cart-related:added', { bubbles: true, detail: { product: product } })
          );
        })
        .catch(function (e) {
          console.error('[cart-related] add failed', e);
          btn.disabled = false;
          btn.textContent = 'Add';
        });
    });

    return card;
  }

  function renderJsCards(container, products, gap) {
    container.innerHTML = '';
    var frag = document.createDocumentFragment();
    products.forEach(function (p) {
      frag.appendChild(buildJsCard(p, gap));
    });
    container.appendChild(frag);
  }

  /* Render the selected products as themed cards into `container`. Each card is
     wrapped in a cell of `cellClass` (default 'cart-related__cell'; pass
     'swiper-slide' to feed Hyper's carousel). Order is preserved. */
  function renderThemedCards(container, products, sectionId, cache, cellClass) {
    cellClass = cellClass || 'cart-related__cell';
    container.innerHTML = '';
    return Promise.all(
      products.map(function (p) {
        return fetchThemedCardHTML(p.handle, sectionId, cache).then(function (html) {
          return { product: p, html: html };
        });
      })
    ).then(function (results) {
      var frag = document.createDocumentFragment();
      results.forEach(function (res) {
        if (!res.html) return;
        var cell = document.createElement('div');
        cell.className = cellClass;
        cell.setAttribute('data-product-id', res.product.id);
        cell.innerHTML = res.html;
        frag.appendChild(cell);
      });
      container.appendChild(frag);
    });
  }

  /* ------------------------------------------------------ public namespace */

  window.CartRelated = {
    DEFAULTS: DEFAULTS,
    RV_KEY: RV_KEY,
    root: root,
    formatMoney: formatMoney,
    imageUrl: imageUrl,
    thresholdCents: thresholdCents,
    getRecentlyViewed: getRecentlyViewed,
    fetchCart: fetchCart,
    fetchRecommendations: fetchRecommendations,
    fetchProductByHandle: fetchProductByHandle,
    selectProducts: selectProducts,
    addProduct: addProduct,
    addToCart: addToCart,
    buildJsCard: buildJsCard,
    renderJsCards: renderJsCards,
    renderThemedCards: renderThemedCards,
  };

  /* ------------------------------------ <cart-recommendations-extended> */

  /*
   * "Cart recommendations - extended" — drop-in upgrade of Hyper's own cart
   * recommendation widget. Reuses the vendor widget's markup, card style
   * (card-product-list) and FoxTheme carousel, but swaps the data source from
   * Hyper's single-seed intent=complementary fetch to OUR pipeline (multi-seed
   * merge across the whole cart, intent=related, gap scoring, recently-viewed).
   *
   * The host snippet (snippets/custom-cart-recommendations-extended.liquid)
   * provides the vendor chrome — header, the `.cart-drawer-products-recommendation__container`
   * (with data-columns), the `.cart-drawer-products-recommendation__wrapper` grid,
   * and the nav controls — and carries config via data-* attributes. This
   * component fills the wrapper with themed cards and inits the carousel.
   *
   *   <cart-recommendations-extended
   *     data-minimum-amount="80" data-intent="related" data-limit="6"
   *     data-max-seeds="4" data-hide-when-qualified="true"
   *     data-include-recently-viewed="true"
   *     data-card-section-id="custom-cart-related-card">
   *     …vendor widget chrome with __container/__wrapper…
   *   </cart-recommendations-extended>
   */
  class CartRecommendationsExtended extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      // Use the module-scope themed-card cache so cards survive this element
      // being destroyed/recreated when the drawer re-renders on each cart change.
      this._cache = sessionCardCache;
      this._container = this.querySelector('.cart-drawer-products-recommendation__container');
      this._wrapper = this.querySelector('.cart-drawer-products-recommendation__wrapper') || this;
      this._controls = this.querySelector('.swiper-controls');
      this._nextEl = this.querySelector('.swiper-button-next');
      this._prevEl = this.querySelector('.swiper-button-prev');
      this._cellClass = this.dataset.cellClass || 'swiper-slide';

      // Render eagerly so the cards are ready by the time the drawer opens.
      // (The element must NOT stay display:none waiting on visibility — it starts
      // with the `hidden` attribute, and IntersectionObserver never fires on a
      // display:none element, so an inView-gated run would deadlock and never load.)
      // The CAROUSEL alone is deferred until the element is visible — see
      // scheduleCarousel() — because the cart drawer is display:none until opened
      // and Swiper must measure a real width or its slide sizing + nav break.
      // On every cart change Hyper re-renders the drawer body (cart.js
      // onCartUpdate), which recreates this element and re-runs — so no persistent
      // cart:updated listener is needed.
      this.run();
    }

    disconnectedCallback() {
      if (this._stopInView) {
        try {
          this._stopInView();
        } catch (e) {
          /* ignore */
        }
        this._stopInView = null;
      }
      this.destroyCarousel();
    }

    options() {
      var d = this.dataset;
      return {
        intent: d.intent,
        perSeedLimit: d.perSeedLimit,
        maxSeeds: d.maxSeeds,
        limit: d.limit,
        cardSectionId: d.cardSectionId || 'custom-cart-related-card',
        minimumAmount: d.minimumAmount,
        hideWhenQualified: d.hideWhenQualified !== 'false',
        includeRecentlyViewed: d.includeRecentlyViewed !== 'false',
      };
    }

    hide() {
      this.hidden = true;
    }
    reveal() {
      this.hidden = false;
    }

    columns() {
      return (this._container && Number(this._container.dataset.columns)) || 3;
    }

    isMobile() {
      try {
        if (window.FoxTheme && FoxTheme.config && typeof FoxTheme.config.mqlMobile !== 'undefined') {
          return !!FoxTheme.config.mqlMobile;
        }
        if (window.FoxTheme && FoxTheme.config && FoxTheme.config.mediaQueryMobile) {
          return window.matchMedia(FoxTheme.config.mediaQueryMobile).matches;
        }
      } catch (e) {
        /* ignore */
      }
      return window.matchMedia('(max-width: 767px)').matches;
    }

    /* Mirrors Hyper's CartDrawerProductsRecommendation.initCarousel. Carousel
       only when there are more cards than columns. Grid layout
       (data-enable-slider-mobile="false") inits on desktop only — on mobile the
       vendor's swipe-mobile CSS handles horizontal scroll. Horizontal-list layout
       (data-enable-slider-mobile="true") carousels on mobile + desktop, with
       fraction pagination when a .swiper-pagination element is present. */
    /* Defer carousel init until the element is actually visible (drawer open).
       inView fires immediately if already visible (e.g. cart updated while the
       drawer is open), and we stop observing after the first fire. */
    scheduleCarousel() {
      var self = this;
      if (this._stopInView) {
        try {
          this._stopInView();
        } catch (e) {
          /* ignore */
        }
        this._stopInView = null;
      }
      if (window.FoxTheme && FoxTheme.Motion && FoxTheme.Motion.inView) {
        this._stopInView = FoxTheme.Motion.inView(
          this,
          function () {
            self.initCarousel();
            if (self._stopInView) {
              try {
                self._stopInView();
              } catch (e) {
                /* ignore */
              }
              self._stopInView = null;
            }
          },
          { margin: '200px 0px 200px 0px' }
        );
      } else {
        this.initCarousel();
      }
    }

    initCarousel() {
      if (this._carousel) return; // already initialized for this render
      if (!window.FoxTheme || !FoxTheme.Carousel || !this._container) return;
      var count = this._wrapper.children.length;
      var columns = this.columns();
      if (count <= columns) return;
      var enableSliderMobile = this._container.dataset.enableSliderMobile === 'true';
      if (this.isMobile() && !enableSliderMobile) return;
      try {
        this._container.classList.add('swiper');
        this._wrapper.classList.remove('f-grid');
        this._wrapper.classList.add('swiper-wrapper');
        // Reveal the nav controls BEFORE init so Swiper binds to visible buttons.
        if (this._controls) this._controls.hidden = false;
        var options = {
          slidesPerView: columns,
          spaceBetween: 8,
          loop: false,
          navigation: { nextEl: this._nextEl, prevEl: this._prevEl },
        };
        var paginationEl = this.querySelector('.swiper-pagination');
        if (paginationEl) {
          options.pagination = { el: paginationEl, clickable: true, type: 'fraction' };
        }
        this._carousel = new FoxTheme.Carousel(this._container, options);
        this._carousel.init();
      } catch (e) {
        console.error('[cart-related] carousel init failed', e);
        this.destroyCarousel();
      }
    }

    destroyCarousel() {
      try {
        if (this._carousel && this._carousel.slider && this._carousel.slider.destroy) {
          this._carousel.slider.destroy();
        }
      } catch (e) {
        /* ignore */
      }
      this._carousel = null;
      if (this._container) this._container.classList.remove('swiper');
      if (this._wrapper) {
        this._wrapper.classList.remove('swiper-wrapper');
        this._wrapper.classList.add('f-grid');
      }
      if (this._controls) this._controls.hidden = true;
    }

    run(cart) {
      var self = this;
      var opts = this.options();
      this.destroyCarousel();
      var cartPromise = cart ? Promise.resolve(cart) : fetchCart();

      cartPromise
        .then(function (c) {
          return selectProducts(c, opts, sessionRecsCache);
        })
        .then(function (result) {
          if ((opts.hideWhenQualified && result.qualified) || !result.selected.length) {
            self.hide();
            return;
          }
          self.dispatchEvent(new CustomEvent('cart-related:result', { bubbles: true, detail: result }));
          return renderThemedCards(
            self._wrapper,
            result.selected,
            opts.cardSectionId,
            self._cache,
            self._cellClass
          ).then(function () {
            if (!self._wrapper.children.length) {
              self.hide();
              return;
            }
            self.reveal();
            self.scheduleCarousel();
          });
        })
        .catch(function (e) {
          console.error('[cart-related] run failed', e);
          self.hide();
        });
    }
  }

  if (!customElements.get('cart-recommendations-extended')) {
    customElements.define('cart-recommendations-extended', CartRecommendationsExtended);
  }

  /* ------------------------------------------------- recently-viewed tracker */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordRecentlyViewed);
  } else {
    recordRecentlyViewed();
  }
})();
